import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';

export default function TerminateConfirmDialog({ open, staff, onClose, onConfirm }) {
  const [lastWorkingDate, setLastWorkingDate] = useState('');
  const [terminationReason, setTerminationReason] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      // Set default to today
      const today = new Date().toISOString().split('T')[0];
      setLastWorkingDate(today);
      setTerminationReason('');
      setError('');
    }
  }, [open]);

  const validateDate = () => {
    const selectedDate = new Date(lastWorkingDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      setError('Last working date cannot be in the future');
      return false;
    }

    setError('');
    return true;
  };

  const handleConfirm = async () => {
    if (!validateDate()) {
      return;
    }

    setLoading(true);
    try {
      await onConfirm({ lastWorkingDate, terminationReason });
      onClose();
    } catch (error) {
      console.error('Error terminating employee:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!staff) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2,
          bgcolor: "#2a2a2a",
          color: "#fff"
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#ff9800',
          color: '#000',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <BlockIcon />
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          Terminate Employee
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ mt: 2, bgcolor: "#2a2a2a" }}>
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 2,
            bgcolor: "rgba(255, 152, 0, 0.15)",
            color: "#ff9800",
            "& .MuiAlert-icon": {
              color: "#ff9800"
            }
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            This will prevent the employee from logging in
          </Typography>
        </Alert>

        <Typography variant="body1" sx={{ mb: 2, color: "#fff" }}>
          You are about to terminate the following employee:
        </Typography>

        <Box
          sx={{
            bgcolor: '#1a1a1a',
            p: 2,
            borderRadius: 1,
            mb: 3,
            border: "1px solid #444"
          }}
        >
          <Typography variant="subtitle2" sx={{ color: "#888" }}>
            Name
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: "#C4A574" }}>
            {staff.FullName}
          </Typography>

          <Typography variant="subtitle2" sx={{ color: "#888" }}>
            Designation
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "#C4A574" }}>
            {staff.Designation || 'Not Assigned'}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mb: 2, color: "#ccc" }}>
          <strong>Note:</strong> All historical data (attendance, salary records) will be
          preserved.
        </Typography>

        <TextField
          fullWidth
          required
          type="date"
          label="Last Working Date"
          value={lastWorkingDate}
          onChange={(e) => {
            setLastWorkingDate(e.target.value);
            setError('');
          }}
          onBlur={validateDate}
          error={!!error}
          helperText={error || 'Select the last working date'}
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            max: new Date().toISOString().split('T')[0],
          }}
          sx={{ 
            mb: 2,
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#1a1a1a",
              color: "#fff",
              "& fieldset": {
                borderColor: "rgba(196, 165, 116, 0.3)",
              },
              "&:hover fieldset": {
                borderColor: "rgba(196, 165, 116, 0.5)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#C4A574",
              },
            },
            "& .MuiInputLabel-root": {
              color: "#C4A574",
              "&.Mui-focused": {
                color: "#C4A574",
              },
            },
            "& .MuiFormHelperText-root": {
              color: error ? "#ff6b6b" : "#999",
            },
          }}
        />

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Termination Reason (Optional)"
          value={terminationReason}
          onChange={(e) => setTerminationReason(e.target.value)}
          placeholder="Enter reason for termination..."
          helperText="This will be stored in the employee record"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#1a1a1a",
              color: "#fff",
              "& fieldset": {
                borderColor: "rgba(196, 165, 116, 0.3)",
              },
              "&:hover fieldset": {
                borderColor: "rgba(196, 165, 116, 0.5)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#C4A574",
              },
            },
            "& .MuiInputLabel-root": {
              color: "#C4A574",
              "&.Mui-focused": {
                color: "#C4A574",
              },
            },
            "& .MuiFormHelperText-root": {
              color: "#999",
            },
            "& .MuiOutlinedInput-input::placeholder": {
              color: "rgba(255, 255, 255, 0.5)",
              opacity: 1,
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#2a2a2a' }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ color: "#ccc" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!lastWorkingDate || loading}
          sx={{
            bgcolor: '#ff9800',
            color: '#000',
            fontWeight: 600,
            '&:hover': { bgcolor: '#f57c00' },
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: "#000" }} /> : 'Terminate Employee'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
